import axios from "axios";
import { computed, onScopeDispose, ref, watch } from "vue";
import { i18n, trainerDetails } from "../main.ts";
import { getQueryParameter, removeQueryParameter } from "../utils/GeneralUtils.ts";
import { useRoute, useRouter } from "vue-router";
import { LOCALIZED_ATTRIBUTES, manualLanguage } from "./userManualLanguages.ts";
import { convertManual, type ManualConversionOptions } from "./convertManual.ts";

/**
 * Wraps every occurrence of the search term in a <mark> and returns how
 * many were wrapped. Operates on text nodes only, so markup and attribute
 * values can never be corrupted by the injected tags. The TOC and embedded
 * styles are left untouched.
 */
function wrapMatches(doc: Document, lowerCaseSearchTerm: string): number {
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const matchingTextNodes: Text[] = [];
    let current: Node | null;
    while ((current = walker.nextNode())) {
        const textNode = current as Text;
        if (textNode.parentElement?.closest("#toc, style, script")) {
            continue;
        }
        if (textNode.nodeValue?.toLowerCase()?.includes(lowerCaseSearchTerm)) {
            matchingTextNodes.push(textNode);
        }
    }

    let matchCount = 0;
    matchingTextNodes.forEach((textNode) => {
        const text = textNode.nodeValue ?? "";
        const lowerCaseText = text.toLowerCase();
        const fragment = doc.createDocumentFragment();
        let sliceStart = 0;
        let matchIndex = lowerCaseText.indexOf(lowerCaseSearchTerm);
        while (matchIndex !== -1) {
            if (matchIndex > sliceStart) {
                fragment.appendChild(doc.createTextNode(text.slice(sliceStart, matchIndex)));
            }
            const mark = doc.createElement("mark");
            mark.className = "manual-search-highlight";
            mark.textContent = text.slice(matchIndex, matchIndex + lowerCaseSearchTerm.length);
            fragment.appendChild(mark);
            matchCount++;
            sliceStart = matchIndex + lowerCaseSearchTerm.length;
            matchIndex = lowerCaseText.indexOf(lowerCaseSearchTerm, sliceStart);
        }
        if (sliceStart < text.length) {
            fragment.appendChild(doc.createTextNode(text.slice(sliceStart)));
        }
        textNode.parentNode?.replaceChild(fragment, textNode);
    });
    return matchCount;
}

/** A manual screenshot opened in the enlarged view. */
export type ManualImage = { src: string; alt: string };

export function useUserManual() {
    const route = useRoute();
    const router = useRouter();

    const htmlContent = ref("");
    const searchTerm = ref(getQueryParameter(route, "search") || "");
    const cssStyles = ref("");
    const contentContainer = ref<HTMLElement | null>(null);
    const currentMatchIndex = ref(-1);
    const zoomedImage = ref<ManualImage | null>(null);

    const { t } = i18n.global;
    const language = computed(() => i18n.global.locale.value);

    const searchResult = computed(() => {
        if (searchTerm.value.length === 0) {
            return { html: htmlContent.value, matchCount: 0 };
        }
        const lowerCaseSearchTerm = searchTerm.value.toLowerCase();

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent.value, "text/html");

        // Drop chapters without a match, and inside surviving chapters drop the
        // subsections without a match, so only the relevant parts stay visible.
        doc.querySelectorAll("div.sect1").forEach((chapter) => {
            if (chapter.textContent?.toLowerCase()?.includes(lowerCaseSearchTerm) !== true) {
                chapter.remove();
                return;
            }
            chapter.querySelectorAll("div.sect2").forEach((subsection) => {
                if (subsection.textContent?.toLowerCase()?.includes(lowerCaseSearchTerm) !== true) {
                    subsection.remove();
                }
            });
        });

        const visibleHeadingIds = new Set(Array.from(doc.querySelectorAll("h2, h3, h4")).map((heading) => heading.id));

        doc.querySelectorAll("#toc li").forEach((li) => {
            const link = li.querySelector("a");
            const searchElement = link?.getAttribute("href")?.slice(1);
            if (link && searchElement && !visibleHeadingIds.has(searchElement)) {
                link.remove();
                const ul = li.querySelector("ul");
                if (ul) {
                    ul.remove();
                }
            }
        });
        // Drop the now-empty list items (and any lists left without items) so
        // pruned TOC entries don't linger as empty bullets.
        doc.querySelectorAll("#toc li").forEach((li) => {
            if (!li.textContent?.trim()) {
                li.remove();
            }
        });
        doc.querySelectorAll("#toc ul").forEach((ul) => {
            if (ul.children.length === 0) {
                ul.remove();
            }
        });

        const matchCount = wrapMatches(doc, lowerCaseSearchTerm);

        const serializer = new XMLSerializer();
        const updatedHtmlContent = serializer.serializeToString(doc);

        return { html: cssStyles.value + updatedHtmlContent, matchCount };
    });

    const filteredContent = computed(() => searchResult.value.html);
    const matchCount = computed(() => searchResult.value.matchCount);

    // Any re-render of the manual (new term, reload, language switch) rebuilds
    // the marks, so restart the jump cycle.
    watch(filteredContent, () => {
        currentMatchIndex.value = -1;
    });

    /**
     * Registers the element that renders the manual, so jumping targets this
     * composable instance's own content (the page and the dialog can be open
     * at the same time). Bind it via `:ref` on the v-html element.
     */
    function setContentContainer(element: unknown) {
        contentContainer.value = element instanceof HTMLElement ? element : null;
    }

    /**
     * Cycles to the next match (wrapping around at the end) and scrolls it
     * into view. Bound to Enter in the search field, so jumps are always
     * user-initiated — the view never moves while typing.
     */
    function jumpToNextMatch() {
        const marks = contentContainer.value?.querySelectorAll("mark.manual-search-highlight");
        if (!marks || marks.length === 0) {
            return;
        }
        marks[currentMatchIndex.value]?.classList.remove("manual-search-highlight-current");
        currentMatchIndex.value = (currentMatchIndex.value + 1) % marks.length;
        const currentMark = marks[currentMatchIndex.value];
        currentMark.classList.add("manual-search-highlight-current");
        currentMark.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    /**
     * Opens the enlarged view when a screenshot is clicked. Bound to click on
     * the v-html element, because the images are part of the injected markup.
     */
    function openImageFromEvent(event: Event) {
        const image = (event.target as Element | null)?.closest?.(".imageblock img");
        if (image instanceof HTMLImageElement) {
            zoomedImage.value = { src: image.currentSrc || image.src, alt: image.alt };
        }
    }

    function closeImage() {
        zoomedImage.value = null;
    }

    let sectionObserver: IntersectionObserver | undefined;

    /**
     * The sticky contents rail scrolls on its own; bring the current entry back
     * into its visible part. Only the rail moves, never the page (scrollIntoView
     * would also scroll the page). The compact layout has no own scroll area.
     */
    function keepVisibleInContentsRail(link: HTMLElement) {
        const rail = link.closest<HTMLElement>("#toc");
        if (!rail || rail.scrollHeight <= rail.clientHeight) {
            return;
        }
        const top = link.offsetTop;
        const outOfView = top < rail.scrollTop || top + link.offsetHeight > rail.scrollTop + rail.clientHeight;
        if (outOfView) {
            rail.scrollTop = Math.max(0, top - rail.clientHeight / 3);
        }
    }

    /**
     * Highlights the contents entry of the chapter or section being read. A
     * heading counts as current once it enters the top 30% of the viewport.
     * Re-run after every render, because search and language switches replace
     * the whole markup.
     */
    function trackCurrentSection() {
        sectionObserver?.disconnect();
        const container = contentContainer.value;
        if (!container || typeof IntersectionObserver === "undefined") {
            return;
        }
        const tocLinks = Array.from(container.querySelectorAll<HTMLAnchorElement>("#toc a"));
        sectionObserver = new IntersectionObserver(
            (entries) => {
                const current = entries.find((entry) => entry.isIntersecting);
                if (!current) {
                    return;
                }
                const href = `#${current.target.id}`;
                tocLinks.forEach((link) => link.classList.toggle("is-active", link.getAttribute("href") === href));
                const activeLink = tocLinks.find((link) => link.getAttribute("href") === href);
                if (activeLink) {
                    keepVisibleInContentsRail(activeLink);
                }
            },
            { rootMargin: "0px 0px -70% 0px" },
        );
        container.querySelectorAll(".sect1 > h2[id], .sect2 > h3[id]").forEach((heading) => sectionObserver?.observe(heading));
    }

    watch([filteredContent, contentContainer], trackCurrentSection, { flush: "post" });
    onScopeDispose(() => sectionObserver?.disconnect());

    function clearSearchTerm() {
        searchTerm.value = "";
        removeQueryParameter(route, router, "search");
    }

    let latestLoadId = 0;

    async function loadContent() {
        // Snapshot the language and tag the request so a locale switch while a
        // fetch is in flight can neither mix languages nor let a stale response
        // overwrite the newer one.
        const loadId = ++latestLoadId;
        const currentLanguage = manualLanguage(language.value);
        try {
            const docs = await axios.get(`/docs-${currentLanguage}.adoc`);
            const cssResponse = await axios.get("/asciidoc.css");
            if (loadId !== latestLoadId) {
                return;
            }
            const captionAttributes = LOCALIZED_ATTRIBUTES[currentLanguage] ?? {};
            // Sections wrapped in ifdef::distributor[] only render for distributor
            // accounts; for everyone else (incl. logged-out readers) they are dropped
            // together with their TOC entries.
            const distributorAttribute: Record<string, string> =
                trainerDetails.value?.isDistributorAccount === true ? { distributor: "" } : {};
            const conversionOptions: ManualConversionOptions = {
                safe: "safe",
                attributes: {
                    imagesdir: "/images/manual",
                    stylesdir: "/",
                    stylesheet: "asciidoc.css",
                    // UI-label macros (btn:[Save], menu:Use profiles[Create direct link]),
                    // a § deep link on every heading, and reference tables without the
                    // automatic "Table 1." caption prefix.
                    experimental: "",
                    sectanchors: "",
                    "table-caption": null,
                    ...captionAttributes,
                    ...distributorAttribute,
                },
            };
            const convertedHtml = await convertManual(docs.data, conversionOptions);
            // The conversion is asynchronous as well, so a newer load may have
            // finished in the meantime.
            if (loadId !== latestLoadId) {
                return;
            }
            // Injected raw, the stylesheet would be *unlayered*, and unlayered CSS beats
            // every layer regardless of specificity. Confining it to the `asciidoc`
            // layer, declared before `primevue`, keeps it from ever overriding the
            // PrimeVue dialog chrome or our own .app-wrapper.
            cssStyles.value = `<style>@layer asciidoc {${cssResponse.data}}</style>`;
            // asciidoc.css scopes every rule to .manual; .manual-root is the size
            // container that switches to the compact layout in the help dialog.
            htmlContent.value = `${cssStyles.value}<div class="manual-root"><div class="manual">${convertedHtml}</div></div>`;
        } catch (err) {
            console.error(err);
        }
    }

    // Reload the docs when the interface language changes, so an already
    // mounted manual (page or dialog) doesn't keep showing the old language.
    watch(language, () => {
        void loadContent();
    });

    // Trainer details resolve before the app mounts, but re-render defensively in
    // case the account (and with it the distributor flag) changes while a manual
    // is open, e.g. when an impersonation ends.
    watch(
        () => trainerDetails.value?.isDistributorAccount,
        () => {
            void loadContent();
        },
    );

    return {
        htmlContent,
        searchTerm,
        filteredContent,
        matchCount,
        setContentContainer,
        jumpToNextMatch,
        zoomedImage,
        openImageFromEvent,
        closeImage,
        clearSearchTerm,
        loadContent,
        t,
    };
}
